import { CookieConsentOverview } from '../../components/CookieConsentOverview';
import type { LegalDocKey } from '../types';

type LegalDocMeta = Record<
  LegalDocKey,
  {
    title: string;
    url: string;
  }
>;

type LegalModalProps = {
  open: boolean;
  legalDoc: LegalDocKey;
  legalDocs: LegalDocMeta;
  legalHtml: string;
  legalLoading: boolean;
  legalError: string | null;
  onClose: () => void;
};

export function LegalModal({
  open,
  legalDoc,
  legalDocs,
  legalHtml,
  legalLoading,
  legalError,
  onClose,
}: LegalModalProps) {
  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            onClose();
          }
        }}
      />

      <div className="relative w-full max-w-[628px] max-h-[calc(100vh-64px)] overflow-auto rounded-[16px] border border-black/10 bg-white text-[#1C1C1E] shadow-xl">
        <div className="flex items-center justify-between border-b border-black/10 px-8 pb-6 pt-8">
          <div className="font-pangaia text-[32px] font-bold leading-[40px]">
            {legalDocs[legalDoc].title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-4 rounded-full p-2 text-[#1C1C1E] transition hover:bg-black/5"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M8 8L20 20M20 8L8 20" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto px-8 pb-8 pr-6 text-base leading-relaxed">
          {legalLoading && <p className="font-pangram text-sm text-[#636366]">Loading document...</p>}

          {legalError && (
            <div className="space-y-2">
              <p className="font-pangram text-sm text-red-600">{legalError}</p>
              <p className="font-pangram text-sm text-[#636366]">
                Expected file: <span className="font-mono">{legalDocs[legalDoc].url}</span>
              </p>
            </div>
          )}

          {!legalLoading && !legalError && (
            <>
              <div
                className="legal-content text-sm leading-relaxed"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: legalHtml }}
              />
              {legalDoc === 'privacy' && (
                <div className="mt-6 border-t border-black/10 pt-4">
                  <h3 className="mb-3 font-pangaia text-[20px] font-bold leading-[28px]">
                    Your Cookie Consent
                  </h3>
                  <CookieConsentOverview />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
